import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AddToListButton, Header3 } from "@activecollab/components";
import { ReorderableList, reorderArray } from "@activecollab/yank-and-drop";
import PropTypes from "prop-types";
import { reorderProjectTemplateElements } from "~/store/actions/actions.projectTemplateElements";
import { makeGetSubtasksByTaskIdSortedByPosition } from "~/store/selectors/selector.projectTemplateElements";
import TemplateSubtaskForm from "./TemplateSubtaskForm";
import { TemplateSubtaskItem } from "./TemplateSubtaskItem";

export const TemplateSubtasks = ({ taskId, templateId, isHiddenFromClients, className }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const getProjectTemplateSubtasks = useMemo(makeGetSubtasksByTaskIdSortedByPosition, []);
  const reduxSubtasks = useSelector((state) => getProjectTemplateSubtasks(state, taskId));

  const [subtasks, setSubtasks] = useState(reduxSubtasks);
  const [subtaskAddFormOpen, setSubtaskAddFormOpen] = useState(false);

  useEffect(() => {
    setSubtasks(reduxSubtasks);
  }, [reduxSubtasks]);

  const handleMove = useCallback((from, to) => {
    setSubtasks((prevState) => {
      return reorderArray(prevState, from, to);
    });
  }, []);

  const handleReorder = useCallback(() => {
    setSubtasks((currentSubtasks) => {
      const ids = currentSubtasks.map((item) => item.id);
      dispatch(reorderProjectTemplateElements(templateId, ids));
      return currentSubtasks;
    });
  }, [dispatch, templateId]);

  return (
    <div className={className}>
      {subtasks.length ? (
        <>
          <Header3
            className="tw-mb-2"
            weight="bold"
          >
            {t("Subtasks")}
          </Header3>
          <ReorderableList
            onMove={handleMove}
            onReorder={handleReorder}
            dragAxis="y"
            handle=".drag-icon"
            className="tw-mb-1 tw--ml-[34px]"
          >
            {subtasks.map((subtask) => {
              return (
                <TemplateSubtaskItem
                  key={`${subtask.id}=${subtask.name}`}
                  subtask={subtask}
                  isHiddenFromClients={isHiddenFromClients}
                  shouldShowDragHandler={subtasks.length > 1}
                />
              );
            })}
          </ReorderableList>
        </>
      ) : null}

      {subtaskAddFormOpen ? (
        <TemplateSubtaskForm
          templateId={templateId}
          taskId={taskId}
          isHiddenFromClients={isHiddenFromClients}
          handleClose={() => setSubtaskAddFormOpen(false)}
        />
      ) : (
        <AddToListButton
          text={t("Add a Subtask")}
          onClick={() => setSubtaskAddFormOpen(true)}
          className="tw-w-fit tw-mt-1 tw--ml-1"
        />
      )}
    </div>
  );
};

TemplateSubtasks.propTypes = {
  taskId: PropTypes.number.isRequired,
  templateId: PropTypes.number.isRequired,
  isHiddenFromClients: PropTypes.bool,
  className: PropTypes.string,
};
